<template>
    <div>
        <b-modal id="scheduleExportModal" title="Scheduled Exports" size="xl" scrollable hide-footer no-fade @show="onModalShow">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted">Manage scheduled exports for this dashboard</span>
                <b-button variant="primary" size="sm" @click="addSchedule">
                    <i class="fas fa-plus mr-1"></i>
                    Add Schedule
                </b-button>
            </div>

            <div v-if="loadingSchedules" class="text-center py-4">
                <b-spinner />
                Loading schedules...
            </div>

            <div v-else-if="!existingSchedules.length" class="text-center text-muted py-4">
                <i class="fas fa-calendar-alt fa-3x mb-3 d-block"></i>
                No schedules configured for this dashboard yet.
                <br>
                <b-button variant="primary" class="mt-3" @click="addSchedule">
                    <i class="fas fa-plus mr-1"></i>
                    Create Your First Schedule
                </b-button>
            </div>

            <b-table v-else :items="existingSchedules" :fields="scheduleFields" small striped hover responsive>
                <template #cell(enabled)="data">
                    <b-badge :variant="data.value ? 'success' : 'secondary'">
                        {{ data.value ? 'Enabled' : 'Disabled' }}
                    </b-badge>
                </template>

                <template #cell(lastExecutionTime)="data">
                    {{ data.value ? formatDateTime(data.value) : 'Never' }}
                </template>

                <template #cell(actions)="data">
                    <b-button size="sm" variant="success" block class="mb-1" @click="runNow(data.item)"
                        :disabled="runningScheduleId === data.item.id">
                        <b-spinner v-if="runningScheduleId === data.item.id" small class="mr-1" />
                        <i v-else class="fas fa-play mr-1"></i>
                        {{ runningScheduleId === data.item.id ? 'Running...' : 'Run Now' }}
                    </b-button>
                    <b-button size="sm" variant="primary" block class="mb-1" @click="editSchedule(data.item)">
                        <i class="fas fa-edit mr-1"></i> Edit
                    </b-button>
                    <b-button size="sm" variant="info" block class="mb-1" @click="viewHistory(data.item)">
                        <i class="fas fa-history mr-1"></i> History
                    </b-button>
                    <b-button size="sm" variant="danger" block @click="deleteSchedule(data.item)">
                        <i class="fas fa-trash mr-1"></i> Delete
                    </b-button>
                </template>
            </b-table>
        </b-modal>

        <ScheduleFormModal
            :dashboard-id="dashboardId"
            :dashboard-title="dashboardTitle"
            :editing-schedule="selectedSchedule"
            @saved="onScheduleSaved"
        />

        <ExportHistoryModal
            v-if="selectedHistorySchedule"
            :schedule-id="selectedHistorySchedule.id"
            :schedule-name="selectedHistorySchedule.name"
        />
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import ScheduleFormModal from "./ScheduleFormModal.vue";
import ExportHistoryModal from "./ExportHistoryModal.vue";
import { ReportExportSchedule, reportExportService } from "@/services/reportExportService";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    components: {
        ScheduleFormModal,
        ExportHistoryModal
    }
})
export default class ScheduleExportModal extends Vue {
    @Prop({ required: true }) dashboardId!: string;
    @Prop({ default: "" }) dashboardTitle!: string;

    private loadingSchedules = false;
    private existingSchedules: ReportExportSchedule[] = [];
    private selectedSchedule: ReportExportSchedule | null = null;
    private selectedHistorySchedule: ReportExportSchedule | null = null;
    private runningScheduleId: number | null = null;

    private scheduleFields = [
        { key: "name", label: "Name", sortable: true },
        { key: "exportFormat", label: "Format" },
        { key: "schedule", label: "Schedule" },
        { key: "enabled", label: "Status" },
        { key: "lastExecutionTime", label: "Last Run", sortable: true },
        { key: "actions", label: "", thStyle: { width: "140px" } }
    ];

    async onModalShow() {
        await this.loadExistingSchedules();
    }

    private async loadExistingSchedules() {
        this.loadingSchedules = true;
        try {
            this.existingSchedules = await reportExportService.listSchedules(this.dashboardId);
        } catch (error) {
            console.error("Failed to load schedules:", error);
        } finally {
            this.loadingSchedules = false;
        }
    }

    private addSchedule() {
        this.selectedSchedule = null;
        this.$nextTick(() => {
            this.$bvModal.show("scheduleFormModal");
        });
    }

    private editSchedule(schedule: ReportExportSchedule) {
        this.selectedSchedule = schedule;
        this.$nextTick(() => {
            this.$bvModal.show("scheduleFormModal");
        });
    }

    private viewHistory(schedule: ReportExportSchedule) {
        this.selectedHistorySchedule = schedule;
        this.$nextTick(() => {
            this.$bvModal.show("exportHistoryModal");
        });
    }

    private async deleteSchedule(schedule: ReportExportSchedule) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Are you sure you want to delete the schedule "${schedule.name}"?`,
            {
                title: "Delete Schedule",
                okVariant: "danger",
                okTitle: "Delete",
                cancelTitle: "Cancel"
            }
        );

        if (!confirmed) return;

        try {
            await reportExportService.deleteSchedule(schedule.id);
            this.$notification(NotificationOptions.success("Schedule deleted successfully"));
            await this.loadExistingSchedules();

            if (this.selectedHistorySchedule?.id === schedule.id) {
                this.selectedHistorySchedule = null;
            }
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error.response?.data?.message || "Failed to delete schedule"));
        }
    }

    private async runNow(schedule: ReportExportSchedule) {
        this.runningScheduleId = schedule.id;
        try {
            const result = await reportExportService.executeSchedule(schedule.id);
            this.$notification(NotificationOptions.success(result.message || "Export started successfully"));
            await this.loadExistingSchedules();
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error.response?.data?.message || "Failed to execute schedule"));
        } finally {
            this.runningScheduleId = null;
        }
    }

    private async onScheduleSaved() {
        await this.loadExistingSchedules();
    }

    private formatDateTime(dateStr: string): string {
        if (!dateStr) return "Never";
        return new Date(dateStr).toLocaleString();
    }
}
</script>
